import React, { ReactNode, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import styled from "styled-components";
import Branding from "../../config/Branding";
import EventStreamControls from "./EventStreamControls";
import EventStreamFilters from "./EventStreamFilters";
import EventStreamList from "./EventStreamList";
import { useEventStream } from "./useEventStream";

const MainRow = styled(Row)<{ standalone?: number }>`
    background: ${Branding.transparentBackground};
    top: ${props => props.standalone ? "30px" : "63px"};
    margin: 10px;
`;

type EventStreamProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
}

type EventStreamState = {
    selectedEventType: string;
    userIdFilter: string;
}

class EventStream extends React.Component<EventStreamProps, EventStreamState> {
    constructor(props: EventStreamProps) {
        super(props);
        this.state = {
            selectedEventType: "",
            userIdFilter: ""
        };
    }

    public render(): ReactNode {
        return (
            <div>
                <Container className="mw-100" key="central">
                    <MainRow className="justify-content-center sticky-banner" standalone={this.props.standalone ? 1 : 0}>
                        <Col md>
                            <Row>
                                <Col style={{ padding: "5px" }}>
                                    <h5>Event Stream</h5>
                                    <EventStreamInner
                                        customerConfig={this.props.customerConfig}
                                        standalone={this.props.standalone}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </MainRow>
                </Container>
            </div>
        );
    }
}

type EventStreamInnerProps = {
    customerConfig: CustomerConfig;
    standalone?: boolean;
}

const EventStreamInner: React.FC<EventStreamInnerProps> = ({ customerConfig }) => {
    const wsConfigured = !!customerConfig.eventStreamWsUrl;
    const { events, isConnected, secondsRemaining, start, stop, clear, error } = useEventStream(
        customerConfig.eventStreamWsUrl
    );

    const [selectedEventType, setSelectedEventType] = useState("");
    const [userIdFilter, setUserIdFilter] = useState("");

    const availableEventTypes = useMemo(
        () => Array.from(new Set(events.map(e => e.action))).sort(),
        [events]
    );

    const filteredEvents = useMemo(() => {
        return events.filter(e => {
            const matchType = selectedEventType === "" || e.action === selectedEventType;
            const matchUser = userIdFilter === "" || e.user_id.toLowerCase().includes(userIdFilter.toLowerCase());
            return matchType && matchUser;
        });
    }, [events, selectedEventType, userIdFilter]);

    const handleStart = () => start(customerConfig.slug);
    const handleFilterChange = (eventType: string, userId: string) => {
        setSelectedEventType(eventType);
        setUserIdFilter(userId);
    };

    return (
        <>
            {!wsConfigured && (
                <p style={{ color: "#999" }}>WebSocket not configured for this customer.</p>
            )}
            <EventStreamControls
                isConnected={isConnected}
                secondsRemaining={secondsRemaining}
                eventCount={events.length}
                wsConfigured={wsConfigured}
                onStart={handleStart}
                onStop={stop}
                onClear={clear}
                error={error}
            />
            <EventStreamFilters
                availableEventTypes={availableEventTypes}
                selectedEventType={selectedEventType}
                userIdFilter={userIdFilter}
                onChange={handleFilterChange}
                onClear={() => handleFilterChange("", "")}
            />
            <EventStreamList
                events={filteredEvents}
                isConnected={isConnected}
            />
        </>
    );
};

export default EventStream;
